/**
 * EvidenceSnippet Component
 *
 * Collapsible display for evidence spans from scoring results.
 * Shows matched text, rule ID, and position information.
 */

import { useState } from 'react';
import { Code } from './JsonViewer';
import type { EvidenceSpan } from '../lib/types';

interface EvidenceSnippetProps {
  /** Evidence spans from scoring result */
  evidence: EvidenceSpan[];
  /** Maximum items to show before collapsing */
  maxVisible?: number;
}

export function EvidenceSnippet({ evidence, maxVisible = 3 }: EvidenceSnippetProps) {
  const [expanded, setExpanded] = useState(false);

  if (evidence.length === 0) {
    return <span className="text-muted text-small">No evidence collected</span>;
  }

  const visibleEvidence = expanded ? evidence : evidence.slice(0, maxVisible);
  const hasMore = evidence.length > maxVisible;

  return (
    <div>
      <div className="text-small text-muted" style={{ marginBottom: 8 }}>
        Evidence ({evidence.length} span{evidence.length !== 1 ? 's' : ''}):
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {visibleEvidence.map((ev, i) => (
          <EvidenceItem key={i} evidence={ev} />
        ))}
      </div>

      {hasMore && !expanded && (
        <button
          className="btn btn-secondary"
          style={{
            marginTop: 8,
            padding: '4px 8px',
            fontSize: 11,
          }}
          onClick={() => setExpanded(true)}
        >
          Show {evidence.length - maxVisible} more
        </button>
      )}

      {expanded && hasMore && (
        <button
          className="btn btn-secondary"
          style={{
            marginTop: 8,
            padding: '4px 8px',
            fontSize: 11,
          }}
          onClick={() => setExpanded(false)}
        >
          Show less
        </button>
      )}
    </div>
  );
}

function EvidenceItem({ evidence }: { evidence: EvidenceSpan }) {
  return (
    <div
      style={{
        padding: '8px 10px',
        background: evidence.matched ? 'var(--semantic-success-light, #f0fdf4)' : 'var(--bg-muted, #f9fafb)',
        borderRadius: 4,
        border: `1px solid ${evidence.matched ? 'var(--semantic-success-border, #bbf7d0)' : 'var(--border-default, #e5e7eb)'}`,
        fontSize: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        {/* Match indicator */}
        <span
          style={{
            color: evidence.matched ? 'var(--semantic-success-text, #166534)' : 'var(--semantic-error-text, #991b1b)',
            fontWeight: 600,
          }}
        >
          {evidence.matched ? '✓' : '✗'}
        </span>

        {/* Rule ID */}
        <Code>{evidence.ruleId}</Code>

        {/* Position */}
        <span className="text-muted mono" style={{ fontSize: 10 }}>
          pos {evidence.charStart}-{evidence.charEnd}
        </span>

        {/* Segment info if available */}
        {evidence.segmentId !== undefined && (
          <span className="text-muted mono" style={{ fontSize: 10 }}>
            segment #{evidence.segmentId}
            {evidence.segmentStart !== undefined && (
              <> ({evidence.segmentStart.toFixed(1)}s - {evidence.segmentEnd?.toFixed(1)}s)</>
            )}
          </span>
        )}
      </div>

      {/* Matched text */}
      <div
        className="mono"
        style={{
          color: 'var(--text-primary)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 60,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={evidence.matchedText}
      >
        "{evidence.matchedText}"
      </div>
    </div>
  );
}
